import { useCallback, useEffect } from 'react';
import clsx from 'clsx';
import _ from 'lodash';
import { FaCheck } from 'react-icons/fa';
import { useDispatch, useSelector } from 'react-redux';
import { useCookies } from 'react-cookie';
import PropTypes from 'prop-types';

import { useRouter } from 'next/router';
import Dropdown from '../Dropdown';
import Filter from '../Filters/Filter';
import Currencies from '../../constants/Currencies';
import { setTripSearchProperty } from '../../store/slices/tripSearch';
import { CurrencyDropdownVariant, PageLinks } from '../../constants';

const queryString = require('query-string');

const SelectCurrency = ({ variant, filterSpacingClass }) => {
  const router = useRouter();
  const dispatch = useDispatch();
  // eslint-disable-next-line no-unused-vars
  const [cookies, setCookie] = useCookies();
  const { selectedCurrency: selectedCurrencyQuery } = router.query;
  const { currencyCode } = useSelector((state) => state.tripSearch);
  const { doSupportCurrency, id } = useSelector((state) => state.user.travelAgency || {});
  const { travelAgency } = useSelector((state) => state.user);
  const defaultCurrency = travelAgency && travelAgency.settings
    && travelAgency.settings.defaultCurrency;
  const currencyDropdownVariant = travelAgency && travelAgency.siteTheme
  && travelAgency.siteTheme.currencyDropdownVariant;
  const doAlignCurrencyFilterWithOtherFilters = !!(travelAgency && travelAgency.filterSettings
    && travelAgency.filterSettings.doAlignCurrencyFilterWithOtherFilters);
  const handleChangeCurrency = (code) => {
    setCookie(`selectedCurrency:${id}`, code || '', { path: '/' });
    dispatch(setTripSearchProperty({ currencyCode: code }));
    const { code: __, state: ___, ...query } = {
      ...router.query,
      selectedCurrency: code,
    };
    const qs = queryString.stringify(query, { skipNull: true });
    router.push({
      pathname: window.location.pathname,
      query: decodeURIComponent(qs),
    });

    const interval = setInterval(() => {
      if (window.location.search.indexOf(`selectedCurrency=${code}`) > -1) {
        clearInterval(interval);
        router.reload();
      }
    }, 200);
    setTimeout(() => { clearInterval(interval); }, 1000);
  };

  useEffect(() => {
    if (!id) return;
    const selectedCurrencyCookie = cookies[`selectedCurrency:${id}`];

    let currency = defaultCurrency || '';
    if (!_.isUndefined(selectedCurrencyQuery) && !_.isNull(selectedCurrencyQuery)) {
      currency = selectedCurrencyQuery;
    } else if (!_.isUndefined(selectedCurrencyCookie) && !_.isNull(selectedCurrencyCookie)) {
      currency = selectedCurrencyCookie;
    }

    if (!_.isUndefined(currency) && !_.isNull(currency)) dispatch(setTripSearchProperty({ currencyCode: currency || '' }));
  }, [cookies, defaultCurrency, dispatch, id, selectedCurrencyQuery]);

  const getLabel = useCallback(() => {
    const match = _.find(Currencies, (c) => c.code === currencyCode);
    if (match) return match.code || match.name;
    return '';
  }, [currencyCode]);

  const displayCurrencies = (currencies) => (
    <div className="grid lg:grid-cols-2 xl:grid-cols-3 w-full items-center col-span-5 gap-1">
      {currencies.map((c) => (
        <div key={c.code} className={clsx('p-2 rounded flex justify-between items-center gap-3 bg-opacity-10 hover:bg-opacity-10 hover:bg-theme', { 'bg-theme': c.code === currencyCode })} role="button" tabIndex="0" onKeyDown={() => { }} onClick={() => handleChangeCurrency(c.code)}>
          <div>
            <div>
              {c.name}
            </div>
            <div className="text-opacity-50 text-black">
              {c.code}
            </div>
          </div>
          <FaCheck className={clsx('text-theme', { invisible: c.code !== currencyCode })} />
        </div>
      ))}
    </div>
  );

  if (!doSupportCurrency || router.pathname !== PageLinks.Home.url) return <></>;

  return (
    <Filter
      label={getLabel()}
      menuWidthClass="w-max left-0 lg:left-auto"
      filterMaxWidthClass={clsx('max-w-full self-center', { 'border-r-2 border-black border-opacity-25': currencyDropdownVariant === CurrencyDropdownVariant.WITHOUT_BG_BORDER_WITH_RIGHT_BORDER })}
      isFilterEmpty
      disableCloseOnOutsideClick
      tooltipPrependText={`Selected currency${currencyCode ? ` ${Currencies[currencyCode].name}` : ''}`}
      dropdownAligning={doAlignCurrencyFilterWithOtherFilters ? 'left' : 'right'}
      variant={variant || (currencyDropdownVariant === CurrencyDropdownVariant.WITHOUT_BG_BORDER || currencyDropdownVariant === CurrencyDropdownVariant.WITHOUT_BG_BORDER_WITH_RIGHT_BORDER ? 'WithoutBgFilterClasses' : 'standard')}
      textColorClassName="text-inherit"
      filterSpacingClass={filterSpacingClass}
    >
      <Dropdown.Item>
        {displayCurrencies([...Object.values(Currencies)])}
      </Dropdown.Item>
    </Filter>
  );
};

SelectCurrency.propTypes = {
  variant: PropTypes.string,
  filterSpacingClass: PropTypes.string,
};

SelectCurrency.defaultProps = {
  variant: undefined,
  filterSpacingClass: undefined,
};

export default SelectCurrency;
